import { inject, Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { CryptoService } from './crypto.service';

export interface CookieOptions {
  /** Days until the cookie expires. Defaults to session cookie if omitted. */
  expires?: number;
  /** Restrict to a path. Defaults to '/'. */
  path?: string;
  /** Restrict to a domain. */
  domain?: string;
  /** Send only over HTTPS. Auto-enabled in production. */
  secure?: boolean;
  /** SameSite policy. Defaults to 'Strict'. */
  sameSite?: 'Strict' | 'Lax' | 'None';
}

/**
 * SecureCookieService
 *
 * Wraps ngx-cookie-service with AES-256 encryption so that all values
 * stored in cookies are ciphertext — never plain text.
 *
 * All cookie names are also hashed (HMAC-SHA256 prefix) to prevent
 * enumeration of what's stored.
 */
@Injectable({ providedIn: 'root' })
export class SecureCookieService {
  private readonly cookies = inject(CookieService);
  private readonly crypto  = inject(CryptoService);

  // ── Write ───────────────────────────────────────────────

  /**
   * Encrypt `value` and store it as a cookie.
   * String values and objects are both supported.
   */
  set(name: string, value: string | object, options: CookieOptions = {}): void {
    const plainText  = typeof value === 'string' ? value : JSON.stringify(value);
    const cipherText = this.crypto.encrypt(plainText);
    const cookieName = this.hashName(name);

    this.cookies.set(
      cookieName,
      cipherText,
      {
        expires:  options.expires,
        path:     options.path     ?? '/',
        domain:   options.domain,
        secure:   options.secure   ?? false,   // set true when on HTTPS
        sameSite: options.sameSite ?? 'Strict',
      }
    );
  }

  // ── Read ────────────────────────────────────────────────

  /** Retrieve and decrypt a cookie value as a string. Returns null if absent. */
  get(name: string): string | null {
    const cookieName  = this.hashName(name);
    const cipherText  = this.cookies.get(cookieName);
    if (!cipherText) return null;

    try {
      return this.crypto.decrypt(cipherText) || null;
    } catch {
      return null;
    }
  }

  /** Retrieve, decrypt and parse a cookie value as type T. Returns null if absent or parse fails. */
  getObject<T>(name: string): T | null {
    const plain = this.get(name);
    if (!plain) return null;

    try {
      return JSON.parse(plain) as T;
    } catch {
      return null;
    }
  }

  /** Returns true if the cookie exists (regardless of value). */
  has(name: string): boolean {
    return this.cookies.check(this.hashName(name));
  }

  // ── Delete ──────────────────────────────────────────────

  /** Remove a single cookie. */
  delete(name: string, path = '/'): void {
    this.cookies.delete(this.hashName(name), path);
  }

  /** Remove all cookies set by this service (entire domain). */
  deleteAll(path = '/'): void {
    this.cookies.deleteAll(path, undefined, false, 'Strict');
  }

  // ── Private helpers ─────────────────────────────────────

  /**
   * Hash the cookie name so stored cookie names are opaque.
   * Uses the first 16 chars of the HMAC-SHA256 signature.
   */
  private hashName(name: string): string {
    return `ctp_${this.crypto.sign(name).slice(0, 16)}`;
  }
}
